import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import {
  ImportError,
  applyImport,
  parseImport,
  type ImportPlan,
  type MergeStrategy,
} from '@/core/io/importer'
import { useEntriesStore } from '@/core/storage/zustand-store'
import { Button } from '../shadcn/button'
import { Input } from '../shadcn/input'
import { Label } from '../shadcn/label'
import { Modal } from './Modal'

export interface ImportDialogProps {
  open: boolean
  onClose: () => void
}

type Phase = 'choose-file' | 'enter-password' | 'choose-strategy' | 'done'

interface PendingFile {
  content: string
  filename: string
}

export function ImportDialog({ open, onClose }: ImportDialogProps) {
  const currentEntries = useEntriesStore((s) => s.entries)
  const replaceEntries = useEntriesStore((s) => s.replace)

  const [phase, setPhase] = useState<Phase>('choose-file')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [pendingFile, setPendingFile] = useState<PendingFile | null>(null)
  const [password, setPassword] = useState('')
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [strategy, setStrategy] = useState<MergeStrategy>('replace-all')
  const fileInput = useRef<HTMLInputElement>(null)

  const reset = (): void => {
    setPhase('choose-file')
    setError(null)
    setPending(false)
    setPendingFile(null)
    setPassword('')
    setPlan(null)
    setStrategy('replace-all')
    if (fileInput.current) fileInput.current.value = ''
  }

  const handleClose = (): void => {
    reset()
    onClose()
  }

  const handleFile = async (file: File): Promise<void> => {
    setPending(true)
    setError(null)
    try {
      const content = await file.text()
      setPendingFile({ content, filename: file.name })
      await processWith(content, undefined)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao ler arquivo')
    } finally {
      setPending(false)
    }
  }

  const processWith = async (content: string, pw: string | undefined): Promise<void> => {
    try {
      const next = await parseImport({
        fileContent: content,
        ...(pw ? { password: pw } : {}),
        currentEntryIds: currentEntries.map((e) => e.id),
      })
      setPlan(next)
      setPhase('choose-strategy')
    } catch (err) {
      if (err instanceof ImportError && err.code === 'NEEDS_PASSWORD') {
        setPhase('enter-password')
        return
      }
      if (err instanceof ImportError && err.code === 'WRONG_PASSWORD') {
        setError('senha incorreta — tente novamente')
        return
      }
      setError(err instanceof Error ? err.message : 'falha na importação')
    }
  }

  const handleApply = (): void => {
    if (!plan) return
    const next = applyImport({ plan, strategy, currentEntries })
    replaceEntries(next)
    setPhase('done')
    setTimeout(() => handleClose(), 800)
  }

  const footer = (() => {
    if (phase === 'enter-password') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            disabled={pending || password.length === 0}
            onClick={() => {
              if (!pendingFile) return
              setPending(true)
              setError(null)
              void processWith(pendingFile.content, password).finally(() =>
                setPending(false),
              )
            }}
          >
            {pending ? 'Decifrando…' : 'Continuar'}
          </Button>
        </>
      )
    }
    if (phase === 'choose-strategy') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleApply}>Aplicar importação</Button>
        </>
      )
    }
    return null
  })()

  return (
    <Modal
      open={open}
      title="Importar dados"
      description="Plano ou criptografado (.json) — três estratégias de merge."
      onClose={handleClose}
      footer={footer}
    >
      {phase === 'choose-file' ? (
        <div className="space-y-4">
          <label className="border-border hover:border-primary hover:bg-muted/40 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors">
            <Upload className="text-muted-foreground h-6 w-6" />
            <span className="font-display text-xl">Selecionar arquivo</span>
            <span className="text-muted-foreground text-[12px]">
              ou arraste e solte aqui
            </span>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              disabled={pending}
              aria-label="Selecionar arquivo"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleFile(file)
              }}
            />
          </label>
        </div>
      ) : null}

      {phase === 'enter-password' && pendingFile ? (
        <div className="space-y-3">
          <p className="text-sm">
            <span className="font-mono">{pendingFile.filename}</span> está criptografado.
          </p>
          <Input
            type="password"
            value={password}
            autoFocus
            placeholder="senha"
            aria-label="Senha"
            onChange={(e) => setPassword(e.target.value)}
            className="font-mono"
          />
        </div>
      ) : null}

      {phase === 'choose-strategy' && plan ? (
        <div className="space-y-4">
          <div className="bg-muted/40 rounded-md border border-border p-3 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Versão" value={`v${plan.payload.version}`} />
              <Stat label="Ano-base" value={String(plan.payload.anoBase)} />
              <Stat label="Lançamentos" value={String(plan.payload.entries.length)} />
            </div>
            {plan.conflictingIds.length > 0 ? (
              <p className="text-warning mt-2 text-[12px]">
                ⚠ {plan.conflictingIds.length} lançamento(s) com mesmo ID já existem
              </p>
            ) : null}
          </div>

          <fieldset className="space-y-2">
            <Label className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase">
              Estratégia
            </Label>
            <StrategyOption
              value="replace-all"
              checked={strategy === 'replace-all'}
              label="Substituir tudo"
              description="Apaga seus lançamentos atuais e usa apenas os do arquivo."
              onSelect={setStrategy}
            />
            <StrategyOption
              value="merge-preserve"
              checked={strategy === 'merge-preserve'}
              label="Mesclar (preservar existente)"
              description="Em conflito de ID, mantém o lançamento atual."
              onSelect={setStrategy}
            />
            <StrategyOption
              value="merge-overwrite"
              checked={strategy === 'merge-overwrite'}
              label="Mesclar (sobrescrever)"
              description="Em conflito de ID, usa o lançamento do arquivo."
              onSelect={setStrategy}
            />
          </fieldset>
        </div>
      ) : null}

      {phase === 'done' ? (
        <p className="text-success text-sm">Importação concluída.</p>
      ) : null}

      {error ? <p className="text-destructive mt-3 text-sm">{error}</p> : null}
    </Modal>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-[10px] font-medium tracking-widest2 uppercase">
        {label}
      </p>
      <p className="font-mono text-sm">{value}</p>
    </div>
  )
}

function StrategyOption({
  value,
  checked,
  label,
  description,
  onSelect,
}: {
  value: MergeStrategy
  checked: boolean
  label: string
  description: string
  onSelect: (s: MergeStrategy) => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={() => onSelect(value)}
      className={[
        'block w-full rounded-md border p-3 text-left transition-colors',
        checked
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:bg-muted/40',
      ].join(' ')}
    >
      <p className="text-sm font-medium">{label}</p>
      <p className="text-muted-foreground text-[12px]">{description}</p>
    </button>
  )
}
